import { get, set } from "lodash-es";
import { shallowRef } from "vue";
import { Error as BlockError } from "@/editor/error";
import { SyncManager } from "@/utilities/syncManager";
import { Syncable } from "@/utilities/syncable";
import IBlockEditor from "@/editor/blockEditorInterface";

export default abstract class Block implements Syncable {
  protected readonly _id: string;
  protected readonly _type: string;
  protected readonly _error: BlockError;

  protected _editComponent: any;
  protected _renderComponent: any;

  private _editor: IBlockEditor;

  protected constructor(json: any, editor: IBlockEditor) {
    this._id = get(json, "hashid", get(json, "id", null));
    this._type = get(json, "type", null);
    this._editor = editor;
    this._error = new BlockError();

    this._editComponent = shallowRef(null);
    this._renderComponent = shallowRef(null);
  }

  protected init(): any {
    return {};
  }

  /**
   * Get the type of the block
   */
  public abstract getType(): string;

  /**
   * Validate the data, If the data is invalid, throw an error
   */
  public abstract validateOrThrow(): Promise<any>;

  /**
   * Get the component for editing the block
   */
  public abstract getEditComponent(): Promise<any>;

  /**
   * Get the component for rendering the block
   */
  public abstract getRenderComponent(): Promise<any>;

  /**
   * Convert the block to JSON
   */
  abstract toJSON(): any;

  get id(): string {
    return this._id;
  }

  get error(): BlockError {
    return this._error;
  }

  get type(): string {
    return this._type;
  }

  get editor(): IBlockEditor {
    return this._editor;
  }

  setEditor(editor: IBlockEditor) {
    this._editor = editor;
  }

  sync() {
    return SyncManager.sync(this, {}, () => this.editor.emit("updated", this));
  }

  setProperty(property: keyof this, value: any) {
    set(this, property, value);
    this.editor.emit("updated", this);
  }

  getProperty(property: keyof this, defaultValue: any = null): any {
    return get(this, property, defaultValue);
  }

  isTypeOf(type: string): boolean {
    return this.type === type;
  }

  isFirst(): boolean {
    return this.editor.getIndex(this) === 0;
  }

  isLast(): boolean {
    return this.editor.getIndex(this) === this.editor.getBlocks().length - 1;
  }

  getIndex(): number {
    return this.editor.getIndex(this);
  }

  public async validate(): Promise<boolean> {
    this.error.clear();

    try {
      await this.validateOrThrow();

      return true;
    } catch (e: any) {
      if (e.inner) {
        this.error.setErrors(
          e.inner.reduce((acc: Record<string, string>, item: any) => {
            acc[item.path] = item.message;
            return acc;
          }, {}),
        );
      } else {
        console.error(e);
      }
    }

    return false;
  }
}
